import { Injectable, InjectionToken, Signal, inject, signal } from '@angular/core';

import type { CompletionStatus } from '../models/lernapp.models';
import {
  CategorizationQuizState,
  LearnerStateService,
  LernbausteinOpenRequest,
  QuizState,
} from './learner-state.service';

export interface LessonInteractionState {
  readonly lernbausteinOpenRequest: Signal<LernbausteinOpenRequest | null>;

  getLernbausteinStatus(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
  ): CompletionStatus;
  getLessonResumeIndex(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): number;
  requestLernbausteinOpen(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
    targetId?: string,
  ): void;
  markLernbausteinVisited(moduleId: string, lessonId: string, lernbausteinId: string): void;
  markLernbausteinCompleted(moduleId: string, lessonId: string, lernbausteinId: string): void;
  markLernbausteineSkipped(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): void;
  getQuizState(lernbausteinId: string): QuizState | null;
  saveQuizState(lernbausteinId: string, state: QuizState): void;
  clearQuizState(lernbausteinId: string): void;
  getCategorizationQuizState(lernbausteinId: string): CategorizationQuizState | null;
  saveCategorizationQuizState(lernbausteinId: string, state: CategorizationQuizState): void;
  clearCategorizationQuizState(lernbausteinId: string): void;
}

@Injectable({ providedIn: 'root' })
export class PersistentLessonInteractionState implements LessonInteractionState {
  private readonly learnerState = inject(LearnerStateService);
  readonly lernbausteinOpenRequest = this.learnerState.lernbausteinOpenRequest;

  getLernbausteinStatus(moduleId: string, lessonId: string, lernbausteinId: string): CompletionStatus {
    return this.learnerState.getLernbausteinStatus(moduleId, lessonId, lernbausteinId);
  }

  getLessonResumeIndex(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): number {
    return this.learnerState.getLessonResumeIndex(moduleId, lessonId, lernbausteinIds);
  }

  requestLernbausteinOpen(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
    targetId?: string,
  ): void {
    this.learnerState.requestLernbausteinOpen(moduleId, lessonId, lernbausteinId, targetId);
  }

  markLernbausteinVisited(moduleId: string, lessonId: string, lernbausteinId: string): void {
    this.learnerState.markLernbausteinVisited(moduleId, lessonId, lernbausteinId);
  }

  markLernbausteinCompleted(moduleId: string, lessonId: string, lernbausteinId: string): void {
    this.learnerState.markLernbausteinCompleted(moduleId, lessonId, lernbausteinId);
  }

  markLernbausteineSkipped(
    moduleId: string,
    lessonId: string,
    lernbausteinIds: readonly string[],
  ): void {
    this.learnerState.markLernbausteineSkipped(moduleId, lessonId, lernbausteinIds);
  }

  getQuizState(lernbausteinId: string): QuizState | null {
    return this.learnerState.getQuizState(lernbausteinId);
  }

  saveQuizState(lernbausteinId: string, state: QuizState): void {
    this.learnerState.saveQuizState(lernbausteinId, state);
  }

  clearQuizState(lernbausteinId: string): void {
    this.learnerState.clearQuizState(lernbausteinId);
  }

  getCategorizationQuizState(lernbausteinId: string): CategorizationQuizState | null {
    return this.learnerState.getCategorizationQuizState(lernbausteinId);
  }

  saveCategorizationQuizState(lernbausteinId: string, state: CategorizationQuizState): void {
    this.learnerState.saveCategorizationQuizState(lernbausteinId, state);
  }

  clearCategorizationQuizState(lernbausteinId: string): void {
    this.learnerState.clearCategorizationQuizState(lernbausteinId);
  }
}

@Injectable()
export class PreviewLessonInteractionState implements LessonInteractionState {
  private readonly openRequest = signal<LernbausteinOpenRequest | null>(null);
  private readonly quizStates = new Map<string, QuizState>();
  private readonly categorizationStates = new Map<string, CategorizationQuizState>();
  private nextRequestId = 0;

  readonly lernbausteinOpenRequest = this.openRequest.asReadonly();

  getLernbausteinStatus(
    _moduleId: string,
    _lessonId: string,
    _lernbausteinId: string,
  ): CompletionStatus {
    return 'not-started';
  }

  getLessonResumeIndex(
    _moduleId: string,
    _lessonId: string,
    _lernbausteinIds: readonly string[],
  ): number {
    return 0;
  }

  requestLernbausteinOpen(
    moduleId: string,
    lessonId: string,
    lernbausteinId: string,
    targetId?: string,
  ): void {
    this.openRequest.set({
      moduleId,
      lessonId,
      lernbausteinId,
      targetId,
      requestId: ++this.nextRequestId,
    });
  }

  markLernbausteinVisited(
    _moduleId: string,
    _lessonId: string,
    _lernbausteinId: string,
  ): void {}

  markLernbausteinCompleted(
    _moduleId: string,
    _lessonId: string,
    _lernbausteinId: string,
  ): void {}

  markLernbausteineSkipped(
    _moduleId: string,
    _lessonId: string,
    _lernbausteinIds: readonly string[],
  ): void {}

  getQuizState(lernbausteinId: string): QuizState | null {
    return this.quizStates.get(lernbausteinId) ?? null;
  }

  saveQuizState(lernbausteinId: string, state: QuizState): void {
    this.quizStates.set(lernbausteinId, structuredClone(state));
  }

  clearQuizState(lernbausteinId: string): void {
    this.quizStates.delete(lernbausteinId);
  }

  getCategorizationQuizState(lernbausteinId: string): CategorizationQuizState | null {
    return this.categorizationStates.get(lernbausteinId) ?? null;
  }

  saveCategorizationQuizState(lernbausteinId: string, state: CategorizationQuizState): void {
    this.categorizationStates.set(lernbausteinId, structuredClone(state));
  }

  clearCategorizationQuizState(lernbausteinId: string): void {
    this.categorizationStates.delete(lernbausteinId);
  }
}

export const LESSON_INTERACTION_STATE = new InjectionToken<LessonInteractionState>(
  'LESSON_INTERACTION_STATE',
  { factory: () => inject(PersistentLessonInteractionState) },
);
